import React, { useEffect, useRef, useState } from "react";
import ReusableTable from "../../../components/common/table";
import DashboardCards from "./DashboardCards";
import { Avatar, Button, Card, Col, Row, Tooltip } from "antd";
import LineChart from "./LineChart";
import BarChart from "./BarChart";
import DonutChart from "./DonutChart";
import html2pdf from "html2pdf.js";
import {
  getDashboardCount,
  getDashboardPieChart,
  getTop100LeastListened,
  getTop100MostListened,
  getTop10Listeners,
} from "../../../redux/action/dashboard/dashboardAction";
import { useDispatch, useSelector } from "react-redux";
import { UserOutlined } from "@ant-design/icons";

const Dashboard = () => {
  const [mostTableParams, setMostTableParams] = useState({
    page: 1,
    size: 10,
  });

  const [leastTableParams, setLeastTableParams] = useState({
    page: 1,
    size: 10,
  });
  const contentRef = useRef();
  const element = contentRef.current;
  const elementWidth = element?.offsetWidth;
  const elementHeight = element?.scrollHeight + 700;

  const downloadPDF = () => {
    const element = document.getElementById("dashboardData");

    const opt = {
      margin: 10,
      filename: "Dashboard Report.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        width: elementWidth,
        height: elementHeight,
      },
      jsPDF: {
        unit: "px",
        format: [elementWidth, elementHeight],
        orientation: "portrait",
      },
    };

    html2pdf().set(opt).from(element).save();
  };

  const columnsTop10 = [
    {
      title: "SR. NO.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: "80px",
      render: (text, record, index) => index + 1,
    },
    { title: "Name", dataIndex: "name_en", key: "name_en" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Podcast Play",
      dataIndex: "total_listened_count",
      key: "total_listened_count",
      width: "150px",
    },
    {
      title: "Podcast Completed",
      dataIndex: "complete_listened_count",
      key: "complete_listened_count",
      width: "250px",
    },
    {
      title: "Image",
      dataIndex: "profile_pic",
      key: "profile_pic",
      render: (image) =>
        image ? (
          <img
            src={
              image
                ? `${process.env.REACT_APP_API_IMG_URI}/${image}`
                : undefined
            }
            alt={image}
            style={{ width: 50, height: 50, borderRadius: "50%" }}
          />
        ) : (
          <Avatar size={50} icon={<UserOutlined />} />
        ),
      width: "150px",
    },
  ];

  const topMostColumns = [
    {
      title: "SR. NO.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: "80px",
      render: (text, record, index) =>
        (mostTableParams.page - 1) * mostTableParams.size + index + 1,
    },
    {
      title: "Podcast Title",
      dataIndex: "title_en",
      key: "title_en",
    },
    {
      title: "Total Listeners",
      dataIndex: "listener_count",
      key: "listener_count",
      width: "150px",
    },
  ];

  const topLeastColumns = [
    {
      title: "SR. NO.",
      dataIndex: "sr_no",
      key: "sr_no",
      width: "80px",
      render: (text, record, index) =>
        (leastTableParams.page - 1) * leastTableParams.size + index + 1,
    },
    {
      title: "Podcast Title",
      dataIndex: "title_en",
      key: "title_en",
    },
    {
      title: "Total Listeners",
      dataIndex: "listener_count",
      key: "listener_count",
      width: "150px",
    },
  ];

  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);

  useEffect(() => {
    dispatch(getDashboardCount());
    dispatch(getDashboardPieChart());
    dispatch(getTop10Listeners());
  }, [dispatch]);

  useEffect(() => {
    dispatch(getTop100MostListened(mostTableParams));
  }, [dispatch, mostTableParams.page, mostTableParams.size]);

  // useEffect(() => {
  //   dispatch(getTop100LeastListened(leastTableParams));
  // }, [dispatch, leastTableParams.page, leastTableParams.size]);

  const count = dashboardData.count?.data;
  const countLoading = dashboardData.count?.isLoading;
  const pieChart = dashboardData.pieChart?.data;
  const top10 = dashboardData.top10Listeners?.data;
  const top10Loading = dashboardData.top10Listeners?.isLoading;
  const top100Most = dashboardData.top100MostListened?.data;
  const top100MostLoading = dashboardData.top100MostListened?.isLoading;
  const top100Least = dashboardData.top100LeastListened?.data;
  // const top100LeastLoading = dashboardData.top100LeastListened?.isLoading;

  const paginationConfigMost = {
    pageSizeOptions: ["10", "20", "50", "100"],
    showSizeChanger: true,
    showQuickJumper: true,
    total: top100Most?.count ?? 0,
    current: mostTableParams.page,
    pageSize: mostTableParams.size,
    onChange: (page, pageSize) => {
      setMostTableParams((prev) => ({ ...prev, page, size: pageSize }));
    },
  };

  // const paginationConfigLeast = {
  //   pageSizeOptions: ["10", "20", "50", "100"],
  //   showSizeChanger: true,
  //   showQuickJumper: true,
  //   total: top100Least?.count ?? 0,
  //   current: leastTableParams.page,
  //   pageSize: leastTableParams.size,
  //   onChange: (page, pageSize) => {
  //     setLeastTableParams((prev) => ({ ...prev, page, size: pageSize }));
  //   },
  // };
  const handleMostTableChange = (pagination) => {
    setMostTableParams((prev) => ({
      ...prev,
      page: pagination.current,
      size: pagination.pageSize,
    }));
  };

  const handleLeastTableChange = (pagination) => {
    setLeastTableParams((prev) => ({
      ...prev,
      page: pagination.current,
      size: pagination.pageSize,
    }));
  };

  return (
    <>
      <div ref={contentRef} id="dashboardData">
        <div className="dashboardCard mb-3">
          <DashboardCards
            count={count}
            pieChart={pieChart}
            countLoading={countLoading}
            downloadPDF={downloadPDF}
          />
        </div>

        <div className="charts">
          <div className="tableHeading">Podcast Listeners </div>
          <Row gutter={16}>
            <Col xs={24} sm={16}>
              <Card>
                <LineChart />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <DonutChart pieChart={pieChart} />
              </Card>
            </Col>
            <Col xs={24} sm={24}>
              <div className="tableHeading mt-2">
                Podcast Category-wise Listeners & Completion
              </div>
              <Card>
                <BarChart />
              </Card>
            </Col>
          </Row>
        </div>
        <div className="tableHeading mt-2">Top 10 Listener </div>
        <ReusableTable
          columns={columnsTop10}
          data={top10?.rows ?? []}
          loading={top10Loading}
          actionItems={[]}
          paginationConfig={false}
          useFixedHeight={true}
        />
        <div className="tableHeading mt-1">Top 100 Most Listened </div>
        <ReusableTable
          columns={topMostColumns}
          data={top100Most?.rows ?? []}
          loading={top100MostLoading}
          actionItems={[]}
          paginationConfig={{
            ...paginationConfigMost,
            ...mostTableParams.pagination,
          }}
          handleTableChange={handleMostTableChange}
          useFixedHeight={true}
        />
        {/* <div className="tableHeading mt-1">Top 100 Least Listened </div>
        <ReusableTable
          columns={topLeastColumns}
          data={top100Least?.rows ?? []}
          loading={top100LeastLoading}
          actionItems={[]}
          paginationConfig={{
            ...paginationConfigLeast,
            ...leastTableParams.pagination,
          }}
          handleTableChange={handleLeastTableChange}
          useFixedHeight={true}
        /> */}
      </div>
    </>
  );
};

export default Dashboard;
